'use client';

import { forwardRef, useImperativeHandle, useRef } from 'react';
import type { ForwardedRef } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useDragScroll } from '@/shared/hooks/dom/scroll/useDragScroll';
import { useSwipeScroll } from '@/shared/hooks/dom/scroll/useSwipeScroll';
import { Dialog, DialogContent } from '@/shared/ui/dialog';
import btnIcons from '@/shared/assets/sprites/btns.sprite.svg';
import inputIcons from '@/shared/assets/sprites/inputs.sprite.svg';
import type { IGallery } from '@/entities/common/types/gallery';
interface ImageViewerProps {
  images: IGallery[];
  selectedIndex: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
}
const ImageViewer = forwardRef<HTMLDivElement, ImageViewerProps>(({
  images,
  selectedIndex,
  onClose,
  onNavigate
}, ref: ForwardedRef<HTMLDivElement>) => {
  const innerRef = useRef<HTMLDivElement>(null);
  const t = useTranslations();
  useImperativeHandle(ref, () => innerRef.current!);
  const {
    dragHandlers
  } = useDragScroll(innerRef, {
    dragSpeedMultiplier: 1.5,
    dragThreshold: 5
  });
  const currentImage = images[selectedIndex];
  const hasNext = selectedIndex < images.length - 1;
  const hasPrev = selectedIndex > 0;
  const handleNext = () => {
    if (hasNext) {
      onNavigate(selectedIndex + 1);
    }
  };
  const handlePrev = () => {
    if (hasPrev) {
      onNavigate(selectedIndex - 1);
    }
  };
  const swipeHandlers = useSwipeScroll({
    onSwipeLeft: hasNext ? handleNext : undefined,
    onSwipeRight: hasPrev ? handlePrev : undefined,
    threshold: 50
  });
  const handleInteraction = (e: React.MouseEvent | React.KeyboardEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };
  const keyActions = {
    ArrowLeft: handlePrev,
    ArrowRight: handleNext,
    Escape: onClose
  } as const;
  const handleKeyDown = (e: React.KeyboardEvent) => {
    const key = e.key as keyof typeof keyActions;
    if (e.key === 'Enter' || e.key === ' ') {
      handleInteraction(e);
      return;
    }
    keyActions[key]?.();
  };
  return <Dialog open={selectedIndex !== null} onOpenChange={onClose}>
        <DialogContent className="flex h-screen max-h-screen w-screen max-w-none items-center justify-center bg-black/80 p-0" onKeyDown={handleKeyDown}>
          <div className="relative flex size-full items-center">
            <div className="absolute z-10 flex md:h-full md:w-32">
              {hasPrev && <button onClick={handlePrev} className="ml-1 flex items-center justify-center rounded-full bg-black/60 text-white opacity-70 transition-opacity hover:opacity-100 md:mr-0 md:size-full md:rounded-none md:bg-black/20 md:px-4" aria-label={t('shared.aria.prev')}>
                  <svg className="size-6 -rotate-90 rounded-full fill-textPrimary md:size-10">
                    <use xlinkHref={`${inputIcons}#chevron-up`}></use>
                  </svg>
                </button>}
            </div>

            <div role="button" tabIndex={0} className="relative flex h-full flex-1 items-center justify-center outline-none" ref={innerRef} onClick={handleInteraction} onKeyDown={handleKeyDown} {...dragHandlers} {...swipeHandlers} aria-label={t('shared.aria.close')}>
              <div className="flex flex-col items-center justify-center" role="presentation">
                <button onClick={onClose} className="absolute right-1 top-1 z-50 flex size-8 items-center justify-center rounded-full bg-black/50 text-white opacity-70 transition-opacity hover:opacity-100 md:relative md:-right-84 md:top-1" aria-label={t('shared.aria.close')}>
                  <svg className="size-12 rotate-180 stroke-white">
                    <use xlinkHref={`${btnIcons}#close`}></use>
                  </svg>
                </button>

                <Image width={648} height={420} src={`${currentImage.original}`} alt={t('shared.aria.image_alt', {
              0: selectedIndex + 1
            })} className="rounded-t-2xl object-contain md:max-h-[90vh] md:max-w-[calc(100vw-16rem)]" draggable={false} />

                <div className="w-full rounded-b-2xl bg-textPrimary py-4 pl-6 text-textSecondary" role="status">
                  {t('medcenter.gallery_counter', {
                total: images.length,
                current: selectedIndex + 1
              })}
                </div>
              </div>
            </div>

            <div className="absolute right-0 flex md:h-full md:w-32">
              {hasNext && <button onClick={handleNext} className="mr-1 flex items-center justify-center rounded-full bg-black/60 text-white opacity-70 transition-opacity hover:opacity-100 md:mr-0 md:size-full md:rounded-none md:bg-black/20 md:px-4" aria-label={t('shared.aria.next')}>
                  <svg className="size-6 rotate-90 rounded-full fill-textPrimary md:size-10">
                    <use xlinkHref={`${inputIcons}#chevron-up`}></use>
                  </svg>
                </button>}
            </div>
          </div>
        </DialogContent>
      </Dialog>;
});
ImageViewer.displayName = 'ImageViewer';
export { ImageViewer };